/**
 * One message in a chat: mine on the right in the accent colour, others on the left; everyone's
 * profile picture and name show at the first message of a run (mine on the right side), then a reply
 * quote, time and "breytt". Tapping the bubble shows its actions (Svara,
 * Breyta for my own, Eyða for my own — editors / admins any). Unsent messages show "Sendi…", "Bíður eftir
 * neti" or "Ekki sent" with Reyna aftur / Henda.
 */
import { CloudOff, CornerDownRight, Pencil, Reply, RotateCcw, Trash2, TriangleAlert, type LucideIcon } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { CHAT_MESSAGE_MAX } from '../../shared/chats';
import { Avatar, SectionBadge } from '../components/people';
import { Button, cx, TextArea } from '../components/ui';
import { useI18n, useT } from '../i18n';
import { formatTime } from '../lib/dates';
import { Linkified } from '../screens/calendar/shared';
import type { ChatItem } from './logic';

function ActionButton({ icon: Icon, label, onClick, danger }: { icon: LucideIcon; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'inline-flex min-h-11 items-center gap-1.5 rounded-xl px-2.5 text-[15px] font-semibold transition hover:bg-[var(--accent-soft)]',
        danger ? 'text-[var(--danger-text)]' : 'text-[var(--ink-2)]',
      )}
    >
      <Icon className="size-4.5" aria-hidden />
      {label}
    </button>
  );
}

export interface ChatMessageProps {
  m: ChatItem;
  mine: boolean;
  first: boolean;
  last: boolean;
  /** Show the author's voice badge (the choir chat; in a voice chat everyone shares it). */
  showSection: boolean;
  selected: boolean;
  canDelete: boolean;
  onSelect: (id: string | null) => void;
  onReply: (m: ChatItem) => void;
  onEdit: (id: string, body: string) => Promise<boolean>;
  onDelete: (m: ChatItem) => void;
  onRetry: (m: ChatItem) => void;
  onDiscard: (m: ChatItem) => void;
}

export function ChatMessage({ m, mine, first, last, showSection, selected, canDelete, onSelect, onReply, onEdit, onDelete, onRetry, onDiscard }: ChatMessageProps) {
  const t = useT();
  const { loc } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  if (m.body === null) return null;

  const a = m.author;
  const unsent = m.local === 'queued' || m.local === 'failed';
  const toggle = () => onSelect(selected ? null : m.id);
  const onClick = (e: MouseEvent) => {
    // Links open; text can still be selected without toggling.
    if ((e.target as HTMLElement).closest('a') || window.getSelection()?.toString()) return;
    toggle();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'Escape' && selected) onSelect(null);
  };

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    if (body === m.body) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onEdit(m.id, body);
    setSaving(false);
    if (ok) setEditing(false);
  };

  // The picture at the first message of a run; later ones keep its column.
  const picture = first ? (
    <Avatar userId={a.id} name={a.fullName} rev={a.avatarRev} section={a.section} size={36} className="mt-6" />
  ) : (
    <span className="w-9 shrink-0" aria-hidden />
  );

  return (
    <li className={cx('flex gap-2', mine ? 'justify-end' : 'justify-start', first ? 'mt-3' : 'mt-1')} data-message-id={m.id}>
      {mine ? null : picture}
      <div className={cx('flex max-w-[85%] min-w-0 flex-col md:max-w-[70%]', mine ? 'items-end' : 'items-start', editing && 'w-full')}>
        {first ? (
          <div className={cx('mb-0.5 flex flex-wrap items-center gap-1.5 px-1 text-[14px]', mine && 'justify-end')}>
            <span className={cx('font-semibold', a.inactive && 'text-[var(--muted)]')}>
              {a.fullName}
              {a.inactive ? ` ${t('chat.left')}` : ''}
            </span>
            {showSection ? <SectionBadge section={a.section} title={a.title} short /> : null}
          </div>
        ) : null}

        {editing ? (
          <div className="flex w-full flex-col gap-2">
            <TextArea value={draft} maxLength={CHAT_MESSAGE_MAX} rows={3} onChange={(e) => setDraft(e.target.value)} autoFocus aria-label={t('common.edit')} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" className="min-h-11" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" className="min-h-11" busy={saving} disabled={!draft.trim()} onClick={() => void save()}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        ) : (
          <div
            role="group"
            tabIndex={0}
            aria-label={t('chat.actions_for', { name: mine ? t('chat.you') : a.fullName })}
            onClick={onClick}
            onKeyDown={onKeyDown}
            className={cx(
              'cursor-pointer rounded-2xl px-3.5 py-2 text-[17px] break-words whitespace-pre-wrap outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]',
              mine
                ? 'bg-[var(--accent)] text-[var(--accent-ink)] [&_a]:text-[var(--accent-ink)]'
                : 'border border-[var(--line)] bg-[var(--surface)] text-[var(--ink)]',
              mine ? (last ? 'rounded-br-md' : '') : last ? 'rounded-bl-md' : '',
              m.local === 'sending' && 'opacity-70',
              selected && 'ring-2 ring-[var(--focus)]',
            )}
          >
            {m.replyTo ? (
              <span className="mb-1 flex items-start gap-1.5 border-l-4 border-current py-0.5 pl-2 text-[15px] opacity-85">
                <CornerDownRight className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span className="min-w-0">
                  {m.replyTo.excerpt !== null ? (
                    <>
                      <span className="font-semibold">{m.replyTo.authorName}: </span>
                      {m.replyTo.excerpt}
                    </>
                  ) : (
                    <span className="italic">{t('chat.deleted')}</span>
                  )}
                </span>
              </span>
            ) : null}
            <Linkified text={m.body} />
          </div>
        )}

        {!editing && (last || selected || m.local) ? (
          <span className="px-1 pt-0.5 text-[13px] text-[var(--muted)]">
            {m.local === 'sending' ? t('chat.sending') : formatTime(m.createdAt, loc)}
            {m.editedAt ? ` · ${t('chat.edited')}` : ''}
          </span>
        ) : null}

        {unsent ? (
          <div className={cx('flex flex-wrap items-center gap-1', mine && 'justify-end')}>
            <span className={cx('inline-flex items-center gap-1.5 text-[14px] font-semibold', m.local === 'failed' ? 'text-[var(--danger-text)]' : 'text-[var(--ink-2)]')}>
              {m.local === 'failed' ? <TriangleAlert className="size-4" aria-hidden /> : <CloudOff className="size-4 text-[var(--warn-text)]" aria-hidden />}
              {m.local === 'failed' ? t('chat.failed') : t('chat.queued')}
            </span>
            <ActionButton icon={RotateCcw} label={t('common.retry')} onClick={() => onRetry(m)} />
            <ActionButton icon={Trash2} label={t('chat.discard')} onClick={() => onDiscard(m)} danger />
          </div>
        ) : null}

        {selected && !editing && !m.local ? (
          <div className={cx('flex flex-wrap items-center', mine ? 'justify-end' : '-ml-2.5')}>
            <ActionButton
              icon={Reply}
              label={t('chat.reply')}
              onClick={() => {
                onSelect(null);
                onReply(m);
              }}
            />
            {mine ? (
              <ActionButton
                icon={Pencil}
                label={t('common.edit')}
                onClick={() => {
                  setDraft(m.body ?? '');
                  setEditing(true);
                  onSelect(null);
                }}
              />
            ) : null}
            {mine || canDelete ? (
              <ActionButton
                icon={Trash2}
                label={t('common.delete')}
                onClick={() => {
                  onSelect(null);
                  onDelete(m);
                }}
                danger
              />
            ) : null}
          </div>
        ) : null}
      </div>
      {mine ? picture : null}
    </li>
  );
}
